import { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import { Period, Record, Session } from "./types";

import {
  getRecords,
  insertRecord,
  deleteRecord,
  updateRecord,
} from "./lib/records";
import { getPeriods, insertPeriod, updatePeriod } from "./lib/period";

import PeriodList from "./sections/PeriodList/PeriodList";
import PeriodViewer from "./sections/PeriodViewer/PeriodViewer";
import AddRecordSection from "./sections/Controls/AddRecordSection";
import Modal from "./components/Modal";
import EndPeriodForm from "./components/EndPeriodForm";
import StartPeriod from "./components/StartPeriod";
import MaterialIcon from "./components/MaterialIcon";
import Header from "./components/Header";

interface AppProps {
  session: Session;
  handleLogout: () => void;
}

function App({ session, handleLogout }: AppProps) {
  // UI Control
  let [loading, setLoading] = useState<boolean>(true);
  let [recordsLoading, setRecordsLoading] = useState<boolean>(false);
  let [endPeriodModal, setEndPeriodModal] = useState<boolean>(false);
  let [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  // Data
  let [periods, setPeriods] = useState<Period[]>([]);
  let [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  let [records, setRecords] = useState<Record[]>([]);

  let tableRef = useRef<HTMLTableElement>(null);

  let refreshPeriods = useCallback(async () => {
    try {
      let periods = await getPeriods();
      setPeriods(periods);
      if (!selectedPeriod && periods.length > 0) {
        setSelectedPeriod(0);
      }
    } catch (err) {
      console.error(err);
      alert("Error loading periods!");
    }
  }, [selectedPeriod]);

  let refreshRecords = useCallback(async () => {
    if (selectedPeriod === null) {
      // Error out maybe?
      return;
    }
    let data = await getRecords(periods[selectedPeriod].id);
    setRecords(data);
  }, [periods, selectedPeriod]);

  // Reload periods whenver session changes
  useEffect(() => {
    let asyncCallback = async () => {
      await refreshPeriods();
      setLoading(false);
    };

    asyncCallback();
  }, [session, refreshPeriods]);

  useEffect(() => {
    console.log("Loading records");
    if (selectedPeriod === null) {
      return;
    }

    let asyncCallback = async () => {
      let timeout = setTimeout(() => {
        setRecordsLoading(true);
      }, 100);
      await refreshRecords();
      clearTimeout(timeout);
      setRecordsLoading(false);
    };

    asyncCallback();
  }, [selectedPeriod, refreshRecords]);

  async function handleAddRecord(record: Record): Promise<Record> {
    if (selectedPeriod === null) {
      throw Error("No selected period or session");
    }

    record.period_id = periods[selectedPeriod].id; // replace the temporary ID we gave the object
    let returned_event = await insertRecord(record);
    await refreshRecords();

    return returned_event;
  }

  async function handleUpdateRecord(record: Record) {
    await updateRecord(record);
    await refreshRecords();
  }

  async function handleRemoveRecord(record_id: number) {
    if (selectedPeriod === null) {
      return;
    }

    await deleteRecord(periods[selectedPeriod].id, record_id);
    await refreshRecords();
  }

  async function initFirstPeriod(start_date: string): Promise<Period> {
    let newPeriod = await insertPeriod({ start_date });
    await refreshPeriods();
    return newPeriod;
  }

  // Ends the selected period on the date specified, then creates a new period starting the following day
  async function endPeriod(
    currentPeriodEndDate: string,
    newPeriodName: string,
  ) {
    if (selectedPeriod === null) {
      return;
    }
    await updatePeriod({
      ...periods[selectedPeriod],
      end_date: currentPeriodEndDate,
    });
    let startDate = new Date(currentPeriodEndDate);
    startDate.setDate(startDate.getDate() + 1);
    await insertPeriod({
      name: newPeriodName,
      start_date: startDate.toISOString().slice(0, 10),
      end_date: "",
    });
    setEndPeriodModal(false);
    await refreshPeriods();
  }

  return (
    <>
      {loading ? (
        <div className="mt-20 flex items-center justify-center">
          <MaterialIcon icon="progress_activity" className="animate-spin" />
        </div>
      ) : (
        <>
          <Header
            session={session}
            handleLogout={handleLogout}
            handleMenuClick={() => setSidebarOpen(!sidebarOpen)}
          />
          <main className="flex flex-col items-center gap-4 p-2 pt-4 text-white lg:flex-row lg:items-start lg:p-4">
            {periods.length > 0 && selectedPeriod !== null ? (
              <>
                <PeriodList
                  periods={periods}
                  setSelectedPeriod={setSelectedPeriod}
                  selectedPeriod={selectedPeriod}
                  setEndPeriodModal={(open) => {
                    setEndPeriodModal(open);
                  }}
                  open={sidebarOpen}
                  setOpen={setSidebarOpen}
                  recordTable={tableRef}
                />
                <AddRecordSection
                  addRecord={(record) => handleAddRecord(record)}
                  period={periods[selectedPeriod]}
                  records={records}
                />
                <PeriodViewer
                  period={
                    selectedPeriod !== null ? periods[selectedPeriod] : null
                  }
                  records={records}
                  loading={recordsLoading}
                  handleRemoveRecord={handleRemoveRecord}
                  handleUpdateRecord={handleUpdateRecord}
                  recordTable={tableRef}
                />
                <Modal
                  open={endPeriodModal}
                  closeModal={() => setEndPeriodModal(false)}
                >
                  <EndPeriodForm
                    currentPeriod={periods[selectedPeriod]}
                    handleSubmit={endPeriod}
                  />
                </Modal>
              </>
            ) : (
              <>
                <StartPeriod
                  handleSubmit={(start_date) => initFirstPeriod(start_date)}
                />
              </>
            )}
          </main>
        </>
      )}
    </>
  );
}

export default App;
